// The BuildersLab Business API, for the screens. Signed-in calls go through
// authFetch; the three that work without a session (the shared preview, the
// phone hand-off and nothing else from the browser) use plain fetch.

import { authFetch } from '@/lib/authFetch';
import { supabase } from '@/lib/supabase';

const API_URL = import.meta.env.VITE_API_URL ?? '';
const enc = encodeURIComponent;
const json = body => ({ body: JSON.stringify(body) });

export const businessConfig = () => authFetch('/api/business/config');
export const businessPlans = country => authFetch(`/api/business/plans?country=${enc(country)}`);

export const listCompanies = () => authFetch('/api/companies');
export const createCompany = body => authFetch('/api/companies', { method: 'POST', ...json(body) });
export const getCompany = id => authFetch(`/api/companies/${enc(id)}`);
export const companyLive = (id, { offset = 0, version = 0, since = '' } = {}) =>
  authFetch(`/api/companies/${enc(id)}/live?offset=${offset}&version=${version}${since ? `&since=${enc(since)}` : ''}`);
export const checkNames = (id, names) => authFetch(`/api/companies/${enc(id)}/names/check`, { method: 'POST', ...json({ names }) });
export const chooseName = (id, body) => authFetch(`/api/companies/${enc(id)}/name`, { method: 'PUT', ...json(body) });
export const startBuild = id => authFetch(`/api/companies/${enc(id)}/build`, { method: 'POST' });
export const redoPart = (id, kind, instruction = '') =>
  authFetch(`/api/companies/${enc(id)}/assets/${enc(kind)}/redo`, { method: 'POST', ...json({ instruction }) });
export const shareCompany = id => authFetch(`/api/companies/${enc(id)}/share`, { method: 'POST' });
export const unshareCompany = id => authFetch(`/api/companies/${enc(id)}/share`, { method: 'DELETE' });
export const askCompany = (id, message, history) => authFetch(`/api/companies/${enc(id)}/ask`, { method: 'POST', ...json({ message, history }) });
export const setTaskDone = (id, taskId, done) => authFetch(`/api/companies/${enc(id)}/tasks/${enc(taskId)}`, { method: 'PATCH', ...json({ done }) });
export const writeTask = (id, taskId) => authFetch(`/api/companies/${enc(id)}/tasks/${enc(taskId)}/write`, { method: 'POST' });
export const companyNumbers = id => authFetch(`/api/companies/${enc(id)}/numbers`);
export const applyFounder = (id, answers) => authFetch(`/api/companies/${enc(id)}/founder`, { method: 'POST', ...json({ answers }) });
export const agreeFounder = id => authFetch(`/api/companies/${enc(id)}/founder/agree`, { method: 'POST', ...json({ agree: true }) });

export const getRegistration = id => authFetch(`/api/companies/${enc(id)}/registration`);
export const saveRegistration = (id, form) => authFetch(`/api/companies/${enc(id)}/registration`, { method: 'PUT', ...json({ form }) });
export const uploadRegistrationFile = (id, body) => authFetch(`/api/companies/${enc(id)}/registration/files`, { method: 'POST', ...json(body) });
export const deleteRegistrationFile = (id, fileId) => authFetch(`/api/companies/${enc(id)}/registration/files/${enc(fileId)}`, { method: 'DELETE' });
export const handoffLink = id => authFetch(`/api/companies/${enc(id)}/registration/handoff`, { method: 'POST' });
/**
 * Send the details for checking. Resolves either way: a 422 carries the
 * checks and the fields to fix, which the form shows, so it is not an error.
 * @returns {Promise<{ok:boolean, data:object}>}
 */
export async function submitRegistration(id, form) {
  const { data: { session } } = await supabase.auth.getSession();
  const res = await fetch(`${API_URL}/api/companies/${enc(id)}/registration/submit`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session?.access_token}` },
    ...json({ form }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok && res.status !== 422) throw new Error(data.error || 'Your details could not be sent.');
  return { ok: res.ok, data };
}
export const shareVaultFile = (id, fileId) => authFetch(`/api/companies/${enc(id)}/vault/${enc(fileId)}/share`, { method: 'POST' });

/** A private file's bytes, as an object URL the caller revokes. */
export async function privateFileUrl(path) {
  const { data: { session } } = await supabase.auth.getSession();
  const res = await fetch(`${API_URL}${path}`, { headers: { Authorization: `Bearer ${session?.access_token}` } });
  if (!res.ok) throw new Error('That file could not be opened.');
  return URL.createObjectURL(await res.blob());
}

export const vaultFileUrl = (id, fileId) => privateFileUrl(`/api/companies/${enc(id)}/vault/${enc(fileId)}`);
export const registrationFileUrl = (id, fileId) => privateFileUrl(`/api/companies/${enc(id)}/registration/files/${enc(fileId)}`);

async function publicJson(path, options = {}) {
  const res = await fetch(`${API_URL}${path}`, {
    ...options,
    headers: { 'Content-Type': 'application/json', ...(options.headers || {}) },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw Object.assign(new Error(data.error || 'Request failed'), { status: res.status });
  return data;
}

export const sharedCompany = token => publicJson(`/api/business/shared/${enc(token)}`);
export const handoffInfo = token => publicJson(`/api/business/handoff/${enc(token)}`);
export const handoffUpload = (token, body) => publicJson(`/api/business/handoff/${enc(token)}/files`, { method: 'POST', ...json(body) });

// ── Admin ─────────────────────────────────────────────────────────────────────

export const adminOrders = (status = '') => authFetch(`/api/admin/business/orders${status ? `?status=${enc(status)}` : ''}`);
export const adminOrder = companyId => authFetch(`/api/admin/business/orders/${enc(companyId)}`);
export const adminClaim = (companyId, body = {}) => authFetch(`/api/admin/business/orders/${enc(companyId)}/claim`, { method: 'POST', ...json(body) });
export const adminStatus = (companyId, status) => authFetch(`/api/admin/business/orders/${enc(companyId)}/status`, { method: 'POST', ...json({ status }) });
export const adminQuery = (companyId, message) => authFetch(`/api/admin/business/orders/${enc(companyId)}/query`, { method: 'POST', ...json({ message }) });
export const adminUploadDocument = (companyId, body) => authFetch(`/api/admin/business/orders/${enc(companyId)}/documents`, { method: 'POST', ...json(body) });
export const adminDeleteDocument = (companyId, fileId) => authFetch(`/api/admin/business/orders/${enc(companyId)}/documents/${enc(fileId)}`, { method: 'DELETE' });
export const adminApprove = (companyId, body) => authFetch(`/api/admin/business/orders/${enc(companyId)}/approve`, { method: 'POST', ...json(body) });
export const adminDecideFounder = (companyId, decision) => authFetch(`/api/admin/business/founders/${enc(companyId)}/decide`, { method: 'POST', ...json({ decision }) });
export const adminFileUrl = fileId => privateFileUrl(`/api/admin/business/files/${enc(fileId)}`);

/** Read a picked file as a data URL. */
export function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error('That file could not be read.'));
    reader.readAsDataURL(file);
  });
}
